/**
 * Converts a .NET date/time format pattern (as configured in Viedoc Admin
 * study settings, e.g. "yyyy/MM/dd HH:mm:ss") to a date-fns format pattern.
 *
 * Tokens that differ between .NET and date-fns are mapped (tt -> a,
 * dddd -> EEEE, ...). Letters that are not part of a recognized token are
 * wrapped in single quotes so date-fns treats them as literals instead of
 * throwing on unknown tokens.
 *
 * @param {string} pattern .NET format pattern
 * @returns {string} date-fns format pattern
 */
export function dotNetDateFormatToDateFns(pattern) {
  if (!pattern) {
    return pattern;
  }

  const tokenMap = {
    dddd: "EEEE",
    ddd: "EEE",
    dd: "dd",
    d: "d",
    MMMM: "MMMM",
    MMM: "MMM",
    MM: "MM",
    M: "M",
    yyyy: "yyyy",
    yyy: "yyyy",
    yy: "yy",
    // .NET y is the year without century; date-fns y is the full year.
    // yy is the closest equivalent (differs only in zero-padding for 2000-2009).
    y: "yy",
    HH: "HH",
    H: "H",
    hh: "hh",
    h: "h",
    mm: "mm",
    m: "m",
    ss: "ss",
    s: "s",
    tt: "a",
    t: "a",
    fff: "SSS",
    ff: "SS",
    f: "S",
    zzz: "xxx",
    // .NET z/zz are hour-only offsets ("+2"/"+02"); date-fns has no exact
    // match, x ("+02") is the closest without adding minutes.
    zz: "x",
    z: "x",
  };

  const parts = pattern.match(
    /('[^']*')|("[^"]*")|(\\.)|([a-zA-Z])\4*|([^a-zA-Z'"\\]+)|(.)/g
  );

  return parts
    .map((part) => {
      // Single-quoted literal: already the date-fns escape syntax
      if (part.startsWith("'")) {
        return part;
      }
      // Double-quoted literal: .NET allows it, date-fns only knows single
      // quotes. Inner apostrophes must be doubled to stay escaped.
      if (part.startsWith('"')) {
        return `'${part.slice(1, -1).replace(/'/g, "''")}'`;
      }
      // Backslash-escaped character. An escaped apostrophe becomes '' (the
      // date-fns escape for a literal apostrophe).
      if (part.startsWith("\\")) {
        const character = part.slice(1);
        return character === "'" ? "''" : `'${character}'`;
      }
      // Run of identical letters: map known tokens, quote unknown ones
      if (/^[a-zA-Z]/.test(part)) {
        return tokenMap[part] ?? `'${part}'`;
      }
      // Separators and other non-letter characters pass through.
      // Exception: .NET uses % to mark a single-letter custom pattern
      // (e.g. "%d"); it is never printed, so drop it.
      return part.replace(/%/g, "");
    })
    .join("");
}
